import React from 'react';
import {
  FiBarChart2,
  FiHome,
  FiList,
  FiMap,
  FiMaximize2,
  FiMinimize2,
  FiPackage,
  FiTrendingUp,
  FiTruck,
  FiUsers,
} from 'react-icons/fi';
import CanvasMap from './views/CanvasMap';
import CanvasGrowth from './views/CanvasGrowth';
import CanvasPms from './views/CanvasPms';
import CanvasHosts from './views/CanvasHosts';
import CanvasDrivers from './views/CanvasDrivers';
import CanvasOrders from './views/CanvasOrders';
import CanvasProperties from './views/CanvasProperties';
import { fmtNumber } from './derive';

export const VIEW_DEFS = [
  { id: 'map', label: 'Map', icon: FiMap },
  { id: 'growth', label: 'Growth', icon: FiTrendingUp },
  { id: 'pms', label: 'PMS', icon: FiBarChart2 },
  { id: 'hosts', label: 'Hosts', icon: FiUsers },
  { id: 'drivers', label: 'Drivers', icon: FiTruck },
  { id: 'orders', label: 'Orders', icon: FiPackage },
  { id: 'properties', label: 'Properties', icon: FiHome },
];

function getViewMeta(view, rowCounts) {
  switch (view) {
    case 'map':
      return {
        rowsLabel: `${fmtNumber(rowCounts.map || 0)} orders mapped`,
        recordsAvailable: false,
      };
    case 'growth':
    case 'pms':
    case 'hosts':
      return {
        rowsLabel: `${fmtNumber(rowCounts.hosts || 0)} hosts in scope`,
        recordsAvailable: (rowCounts.hosts || 0) > 0,
      };
    case 'drivers':
      return {
        rowsLabel: `${fmtNumber(rowCounts.drivers || 0)} drivers in scope`,
        recordsAvailable: (rowCounts.drivers || 0) > 0,
      };
    case 'orders':
      return {
        rowsLabel: `${fmtNumber(rowCounts.orders || 0)} orders in scope`,
        recordsAvailable: (rowCounts.orders || 0) > 0,
      };
    case 'properties':
      return {
        rowsLabel: `${fmtNumber(rowCounts.properties || 0)} properties in scope`,
        recordsAvailable: (rowCounts.properties || 0) > 0,
      };
    default:
      return { rowsLabel: '', recordsAvailable: false };
  }
}

export default function DashboardCanvas({
  activeView,
  onChangeView,
  onOpenRecords,
  expanded = false,
  onToggleExpanded,
  rangeLabel,
  summary,
  onSelectHost,
  onSelectPms,
}) {
  const rowCounts = summary?.rowCounts || {};
  const kpis = summary?.kpis || {};
  const leaders = summary?.leaders || {};
  const series = summary?.series || {};
  const breakdowns = summary?.breakdowns || {};
  const meta = getViewMeta(activeView, rowCounts);
  const propertyPmsRows = (breakdowns.propertyPms || []).map((row) => ({
    id: row._id,
    count: row.count,
  }));
  const hostPmsRows = (breakdowns.hostPms || []).map((row) => ({
    id: row._id,
    count: row.count,
    properties:
      breakdowns.propertyPms?.find((propertyRow) => propertyRow._id === row._id)?.count || 0,
  }));

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 px-3 py-2">
        <div className="flex items-center gap-1 overflow-x-auto" role="tablist" aria-label="Dashboard view">
          {VIEW_DEFS.map((viewDef) => {
            const Icon = viewDef.icon;
            const active = activeView === viewDef.id;
            return (
              <button
                key={viewDef.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onChangeView?.(viewDef.id)}
                className={`group inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-[11px] font-medium transition-colors duration-150 motion-reduce:transition-none ${
                  active
                    ? 'bg-zinc-900 text-white shadow-sm'
                    : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                }`}
              >
                <Icon
                  className={`h-3.5 w-3.5 ${
                    active ? 'text-white' : 'text-zinc-400 group-hover:text-zinc-700'
                  }`}
                />
                {viewDef.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10.5px] text-zinc-500">{meta.rowsLabel}</span>
          {onToggleExpanded ? (
            <button
              type="button"
              onClick={() => onToggleExpanded?.()}
              title={expanded ? 'Return to cockpit layout' : 'Make this the main viewport'}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 text-[11px] font-medium text-zinc-700 transition-colors duration-150 hover:bg-zinc-50 motion-reduce:transition-none"
            >
              {expanded ? <FiMinimize2 className="h-3 w-3" /> : <FiMaximize2 className="h-3 w-3" />}
              {expanded ? 'Exit focus' : 'Focus view'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => meta.recordsAvailable && onOpenRecords?.(activeView)}
            disabled={!meta.recordsAvailable}
            title={meta.recordsAvailable ? 'Open records sheet' : 'No records to view'}
            className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 text-[11px] font-medium text-zinc-700 transition-colors duration-150 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
          >
            <FiList className="h-3 w-3" />
            View records
          </button>
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden p-3">
        <div key={activeView} className="h-full animate-[fadeInUp_180ms_ease-out_both] motion-reduce:animate-none">
          {activeView === 'map' ? (
            <CanvasMap onPointClick={() => {}} />
          ) : activeView === 'growth' ? (
            <CanvasGrowth
              series={series.hosts || []}
              rangeLabel={rangeLabel}
              total={rowCounts.growth || 0}
            />
          ) : activeView === 'pms' ? (
            <CanvasPms rows={hostPmsRows} onSelectPms={onSelectPms} />
          ) : activeView === 'hosts' ? (
            <CanvasHosts
              rows={leaders.topHosts || []}
              hostCount={rowCounts.hosts || 0}
              onSelectHost={onSelectHost}
            />
          ) : activeView === 'drivers' ? (
            <CanvasDrivers
              series={series.drivers || []}
              totalInRange={kpis.drivers || 0}
              driverWindows={summary?.driverWindows}
            />
          ) : activeView === 'orders' ? (
            <CanvasOrders
              series={series.orders || []}
              total={kpis.orders || 0}
              revenue={kpis.revenue || 0}
              aov={kpis.aov || 0}
              statusMix={(breakdowns.orderStatus || []).map((row) => ({
                status: row._id,
                count: row.count,
              }))}
            />
          ) : activeView === 'properties' ? (
            <CanvasProperties
              series={series.properties || []}
              rows={propertyPmsRows}
              total={kpis.properties || 0}
              rangeLabel={rangeLabel}
              onSelectPms={onSelectPms}
            />
          ) : null}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
